import { type JSX, type ReactNode } from "react";

import { RoutedErrorBoundary } from "@/components/ErrorBoundary";
import { SidebarProvider } from "@/components/ui/sidebar";
import type { CurrentUser } from "@/models";

import { AppHeader } from "./AppHeader";
import { AppSidebar } from "./AppSidebar";

export interface PageTemplateProps {
  readonly me: CurrentUser;
  /** Page title slot, forwarded to the header. */
  readonly title?: ReactNode;
  /** Page actions slot, forwarded to the header. */
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}

/**
 * The one shell every authenticated route renders inside: collapsible sidebar,
 * breadcrumb header with title and action slots, and a single `main` landmark.
 * Spacing lives here rather than on each page, so two pages cannot drift apart.
 *
 * Full width (#163 R1): no page-level max-width, so content runs from the
 * sidebar edge to a fixed gutter — 16px up to a 1280px viewport, 24px from
 * there. `page-content` is the slot e2e measures that against.
 */
export function PageTemplate({ me, title, actions, children }: PageTemplateProps): JSX.Element {
  return (
    <SidebarProvider>
      <AppSidebar me={me} />
      <div className="flex min-w-0 flex-1 flex-col">
        <AppHeader actions={actions} title={title} />
        <main className="min-w-0 flex-1 px-4 py-6 xl:px-6">
          <div className="flex w-full min-w-0 flex-col gap-4" data-slot="page-content">
            <RoutedErrorBoundary>{children}</RoutedErrorBoundary>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
